import { dispatchAction } from '@ardoq/rxbeach';
import { type Locale } from '@ardoq/locale';
import { isEmail, isValidUrl } from '@ardoq/validation';
import {
  APIFieldType,
  APISurveyAttributes,
  APITagAttributes,
  ArdoqId,
  isPersistedSurvey,
  SurveyApprovalDirectedTriple,
  SurveyQuestionType,
  SurveyResultFilteringType,
  UnpersistedEntity,
  APIOrganizationUser,
  UnpersistedSurveyQuestion,
  SurveySectionStatus,
  PermissionGroup,
  APIFieldAttributes,
  APISurveyPendingApprovals,
  APIComponentAttributes,
  APIReferenceAttributes,
  WithArdoqPersistent,
} from '@ardoq/api-types';
import { getSolutionMetadata, SolutionMetadata } from '@ardoq/core';
import { ReferenceDataLookups } from './referenceUtil';
import {
  MaybePersistedSurvey,
  PendingAnswersInvalidationState,
  PendingAnswersActionForEditedSurvey,
  SurveyValidation,
} from 'surveyAdmin/types';
import { ValidatorFunction } from 'aqTypes';
import {
  checkIfSelectedNewComponentParentDoesNotExist,
  getQuestionValidations,
  hasComponentType,
  hasQuestionValidationOfSeverity,
  isReadonlyField,
} from './questions/utils';
import { ReadonlyFieldData } from './streams/readonlyFieldData$';
import {
  checkIfSurveyConflictsWithChangeApprovalScope,
  currentUserHasNoAdminAccessToWorkspace,
  getComponentTypeAndModelId,
  getFallbackUserAudience,
  questionsHavePendingAnswersInvalidatingChanges,
} from './SurveyEditor/utils';
import { WorkspaceModelDataByWorkspaceId } from 'streams/models/workspaceModelDataByWorkspaceId$';
import { FieldsState, toFieldTypeKey } from 'streams/fields/fields$';
import { emailValidator, numberValidator } from 'scopeData/editors/validators';
import {
  composeSearchQuery,
  SearchFieldNames,
} from 'search/AdvancedSearch/utils';
import { saveNewSurvey } from './streams/actions';
import { surveyAccessControlInterface } from 'resourcePermissions/accessControlHelpers/surveys';
import { getFeatureContext } from 'streams/featureContext$';
import { dateRangeOperations } from '@ardoq/date-range';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { PermissionContext } from '@ardoq/access-control';
import { isEmpty, isEqual } from 'lodash';
import { Features, hasFeature } from '@ardoq/features';
import { SurveyErrorName, SurveyWarningName } from './consts';
import { emptySurvey } from './emptySurvey';
import { isHiddenFieldQuestion } from './questions/questionPredicates';
import {
  getTraversalAudience,
  getUsersAndGroupsAudience,
  hasUserAndGroupsAudience,
} from './ChangeApproval/utils';
import { surveyApi } from '@ardoq/api';
import { isArdoqError } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import {
  fetchPendingAnswers,
  failedToFetchPendingAnswers,
} from './SurveyEditor/streams/actions';
import { isDevelopmentMode } from 'env';

const invalidatingAttributes = new Set<keyof APISurveyAttributes>([
  'questions',
  'workspace',
  'componentTypeName',
  'allowComponentCreation',
  'allowComponentDeletion',
]);

export const PENDING_ANSWERS_NO_DELETION: PendingAnswersInvalidationState = {
  action: PendingAnswersActionForEditedSurvey.WILL_NOT_DELETE,
};

export const PENDING_ANSWERS_FETCH_FAILED: PendingAnswersInvalidationState = {
  action: PendingAnswersActionForEditedSurvey.FAILED_TO_FETCH,
};

const hasInvalidatingAttributeChange = (
  survey: MaybePersistedSurvey,
  newAttributes: Partial<MaybePersistedSurvey>,
  locale: Locale
): boolean => {
  return (Object.keys(newAttributes) as (keyof MaybePersistedSurvey)[])
    .filter(key => invalidatingAttributes.has(key))
    .some(key => {
      if (key !== 'questions') return !isEqual(survey[key], newAttributes[key]);
      return questionsHavePendingAnswersInvalidatingChanges(
        survey[key] ?? [],
        newAttributes[key] ?? [],
        locale
      );
    });
};

export const getNumberOfInvalidatedSurveyChangeApprovals = (
  surveysWithPendingApprovals: Partial<
    Record<string, APISurveyPendingApprovals>
  >,
  survey: APISurveyAttributes,
  newAttributes: Partial<APISurveyAttributes>,
  locale: Locale
): number => {
  if (
    !survey.approvalFlow?.enabled ||
    !hasInvalidatingAttributeChange(survey, newAttributes, locale)
  ) {
    return 0;
  }

  return surveysWithPendingApprovals[survey._id]?.total ?? 0;
};

export const fetchPendingApprovalsSummaryForSurvey = async (
  surveyId: string
): Promise<number | 'error'> => {
  const result = await surveyApi.fetchPendingApprovalsSummary();
  if (isArdoqError(result)) {
    logError(Error(`Error fetching pending approvals summary: ${result}`));
    return 'error';
  }
  return result[surveyId]?.total ?? 0;
};

export const refreshAndGetPendingAnswersInvalidationStateForSurvey = async (
  survey: APISurveyAttributes,
  newAttributes: Partial<MaybePersistedSurvey>,
  locale: Locale
): Promise<PendingAnswersInvalidationState> => {
  if (!survey.approvalFlow?.enabled) return PENDING_ANSWERS_NO_DELETION;
  if (!hasInvalidatingAttributeChange(survey, newAttributes, locale))
    return PENDING_ANSWERS_NO_DELETION;
  dispatchAction(fetchPendingAnswers());
  try {
    const numPendingAnswers = await fetchPendingApprovalsSummaryForSurvey(
      survey._id
    );
    if (numPendingAnswers === 'error') {
      return PENDING_ANSWERS_FETCH_FAILED;
    }
    return numPendingAnswers > 0
      ? {
          action: PendingAnswersActionForEditedSurvey.WILL_DELETE,
          numPendingAnswers,
        }
      : PENDING_ANSWERS_NO_DELETION;
  } catch (error) {
    logError(
      Error(`Unexpected error fetching pending approvals summary: ${error}`)
    );
    return PENDING_ANSWERS_FETCH_FAILED;
  } finally {
    dispatchAction(failedToFetchPendingAnswers());
  }
};

export const isFieldAllowedInSurvey = (field: APIFieldAttributes) =>
  field.type !== APIFieldType.FILE;

export const getSurveySolutionMetadata = (
  survey: MaybePersistedSurvey
): SolutionMetadata => getSolutionMetadata(survey as WithArdoqPersistent);

type GenerateFieldQuestionsArgs = {
  fields: APIFieldAttributes[];
  workspaceId: string | null;
  parentQuestionIsReferenceQuestion: boolean;
  readonlyFieldData: ReadonlyFieldData;
};

export const generateFieldQuestions = ({
  fields,
  workspaceId,
  parentQuestionIsReferenceQuestion,
  readonlyFieldData,
}: GenerateFieldQuestionsArgs) => {
  return dateRangeOperations
    .mergeDateTimeFieldsToDateRangeFields(fields)
    .fields.filter(isFieldAllowedInSurvey)
    .map(field => {
      const fieldName = dateRangeOperations.isDateRangeField(field)
        ? `${field.dateTimeFields.start.name},${field.dateTimeFields.end.name}`
        : field.name;

      return {
        type: SurveyQuestionType.FIELD,
        label: `${field.label}?`,
        description: field.description ?? '',
        properties: { fieldName },
        readonly: workspaceId
          ? isReadonlyField({
              fieldName,
              workspaceId,
              parentQuestionIsReferenceQuestion,
              readonlyFieldData,
            })
          : false,
      };
    });
};

type GenerateReferenceQuestionsArgs = {
  componentId: ArdoqId;
  references: APIReferenceAttributes[];
  componentTypeNamesById: Map<ArdoqId, string | null>;
  referenceTypeNamesById: Map<ArdoqId, string | null>;
};

const generateReferenceQuestions = ({
  componentId,
  references,
  componentTypeNamesById,
  referenceTypeNamesById,
}: GenerateReferenceQuestionsArgs) => {
  return references
    .filter((reference, index, all) => {
      const isOutgoing = reference.source === componentId;
      const firstMatch = all.find(r => {
        const matchingReferenceType = r.type === reference.type;
        const matchingWorkspaces =
          r.rootWorkspace === reference.rootWorkspace &&
          r.targetWorkspace === reference.targetWorkspace;
        const matchingDirection = isOutgoing
          ? r.source === componentId
          : r.target === componentId;
        const sourceTypeName = componentTypeNamesById.get(r.source);
        const targetTypeName = componentTypeNamesById.get(r.target);
        const refSourceTypeName = componentTypeNamesById.get(reference.source);
        const refTargetTypeName = componentTypeNamesById.get(reference.target);
        const matchingComponentTypes =
          sourceTypeName === refSourceTypeName &&
          targetTypeName === refTargetTypeName;

        return (
          matchingReferenceType &&
          matchingWorkspaces &&
          matchingDirection &&
          matchingComponentTypes
        );
      });
      return all.indexOf(firstMatch!) === index;
    })
    .map(reference => {
      const outgoing = reference.source === componentId;
      const otherEndId = outgoing ? reference.target : reference.source;
      const otherEndComponentTypeName =
        componentTypeNamesById.get(otherEndId) ?? null;
      const referenceTypeName =
        referenceTypeNamesById.get(reference._id) ?? null;

      return {
        type: SurveyQuestionType.REFERENCE,
        label: `Has "${referenceTypeName}" connection ${
          outgoing ? 'to' : 'from'
        } ${otherEndComponentTypeName}?`,
        description: '',
        properties: {
          componentTypeName: otherEndComponentTypeName,
          outgoing,
          questions: [],
          referenceTypeName,
          workspaceId: outgoing
            ? reference.targetWorkspace
            : reference.rootWorkspace,
        },
      };
    });
};

type CreateFromSelectedComponentArgs = {
  contextComponent: APIComponentAttributes;
  componentTypeName: string;
  fields: APIFieldAttributes[];
  references: APIReferenceAttributes[];
  componentTypeNamesById: Map<ArdoqId, string | null>;
  referenceTypeNamesById: Map<ArdoqId, string | null>;
  readonlyFieldData: ReadonlyFieldData;
};

export const createFromSelectedComponent = ({
  contextComponent,
  componentTypeName,
  fields,
  references,
  componentTypeNamesById,
  referenceTypeNamesById,
  readonlyFieldData,
}: CreateFromSelectedComponentArgs): void => {
  const workspaceId = contextComponent.rootWorkspace;
  const questions = [
    {
      type: SurveyQuestionType.ATTRIBUTE,
      label: `What is the name of the ${componentTypeName.toLowerCase()}?`,
      description: '',
      properties: { attributeName: 'name' },
      readonly: workspaceId
        ? isReadonlyField({
            fieldName: 'name',
            workspaceId,
            parentQuestionIsReferenceQuestion: false,
            readonlyFieldData,
          })
        : false,
    },
    ...generateFieldQuestions({
      fields,
      workspaceId,
      parentQuestionIsReferenceQuestion: false,
      readonlyFieldData,
    }),
    ...generateReferenceQuestions({
      componentId: contextComponent._id,
      references,
      componentTypeNamesById,
      referenceTypeNamesById,
    }),
  ];

  const newSurvey: UnpersistedEntity<APISurveyAttributes> = {
    ...emptySurvey(),
    name: `Autogenerated survey for ${componentTypeName}`,
    description: `Survey for collecting information about ${componentTypeName}.`,
    workspace: workspaceId,
    newComponentParentId: contextComponent.parent,
    componentSearch: composeSearchQuery({
      attributes: [
        {
          attribute: SearchFieldNames.ROOT_WORKSPACE,
          value: workspaceId,
        },
        {
          attribute: SearchFieldNames.TYPE_NAME,
          value: componentTypeName,
        },
      ],
    }),
    componentTypeName,
    questions: questions as UnpersistedSurveyQuestion[],
  };

  dispatchAction(saveNewSurvey(newSurvey));
};

const getFields = (
  survey: MaybePersistedSurvey,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId,
  fieldsState: FieldsState
) => {
  const { selectedModelId, selectedTypeId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    survey.workspace,
    survey.componentTypeName
  );
  if (!selectedTypeId || !selectedModelId) {
    return [];
  }
  return (
    fieldsState.byComponentType.get(
      toFieldTypeKey(selectedModelId, selectedTypeId)
    ) ?? []
  );
};

export const hasHiddenFieldsError = (
  survey: MaybePersistedSurvey,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId,
  fieldsState: FieldsState
): boolean => {
  const fields = getFields(
    survey,
    workspaceModelDataByWorkspaceId,
    fieldsState
  );
  return survey.questions.some(question => {
    if (isHiddenFieldQuestion(question)) {
      const field = fields.find(
        field => field.name === question.properties.fieldName
      );

      const validators: Partial<Record<APIFieldType, ValidatorFunction>> = {
        [APIFieldType.NUMBER]: numberValidator,
        [APIFieldType.EMAIL]: emailValidator,
      };

      if (field) {
        const validator = validators[field.type];
        return Boolean(validator?.(question.properties.resetValue));
      }
      return true;
    }
    return false;
  });
};

export const isRequiredNewComponentParentMissing = (
  survey: MaybePersistedSurvey,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId
) => {
  const workspaceModelData = survey.workspace
    ? workspaceModelDataByWorkspaceId[survey.workspace]
    : undefined;
  if (!workspaceModelData) {
    return false;
  }
  // It's too early to decide if parent is missing if component type and workspace is not yet selected
  if (!survey.componentTypeName) {
    return false;
  }
  if (!workspaceModelData.modelIsRigid) {
    return false;
  }

  // If model is not flexible and parent is not set, then the selected component type better be the top level type!!
  const isRootComponentType =
    workspaceModelData.rootComponentTypeNames.includes(
      survey.componentTypeName
    );
  const setParentIdOrHasQuestion =
    survey.newComponentParentId ||
    survey.questions.find(
      question =>
        question.type === SurveyQuestionType.ATTRIBUTE &&
        question.properties.attributeName === 'parent'
    );
  return (
    !isRootComponentType &&
    survey.allowComponentCreation &&
    !setParentIdOrHasQuestion
  );
};

const isMissingNameQuestion = (survey: MaybePersistedSurvey) => {
  return (
    survey.allowComponentCreation &&
    !survey.questions.find(
      question =>
        question.type === SurveyQuestionType.ATTRIBUTE &&
        question.properties.attributeName === 'name'
    )
  );
};

const isMissingResultFilteringFields = (survey: MaybePersistedSurvey) => {
  const { enabled, type, properties } = survey.filterSetup ?? {};
  if (!enabled || type !== SurveyResultFilteringType.REFERENCE) return false;
  if (!properties) return true;
  const hasMissingWorkspaceId = !properties.workspaceId;
  const hasMissingComponentTypeName = !properties.componentTypeName;
  const hasMissingReferenceDirection = properties.outgoing === undefined;
  const hasMissingReferenceTypeName = !properties.referenceTypeName;
  return (
    hasMissingWorkspaceId ||
    hasMissingComponentTypeName ||
    hasMissingReferenceDirection ||
    hasMissingReferenceTypeName
  );
};

const checkHasChangeApprovalCompatibilityError = (
  survey: MaybePersistedSurvey
) => {
  const isChangeApprovalOn = survey.approvalFlow?.enabled;
  if (!isChangeApprovalOn) return false;

  return checkIfSurveyConflictsWithChangeApprovalScope(survey);
};

const hasMissingSourceOrTarget = (triple: SurveyApprovalDirectedTriple) =>
  triple.sourceType === null || triple.targetType === null;

const checkHasAdvancedAudienceSelectionError = (
  survey: MaybePersistedSurvey
) => {
  const { approvalFlow } = survey;
  if (!approvalFlow?.enabled || !approvalFlow.selectors) return false;
  const advancedAudiencePaths = getTraversalAudience(approvalFlow.selectors)
    .traversal.paths;
  return advancedAudiencePaths.some(triples =>
    triples.some(hasMissingSourceOrTarget)
  );
};

export const checkIfMissingFallbackUser = (
  survey: MaybePersistedSurvey,
  users: APIOrganizationUser[]
) => {
  const isChangeApprovalOn = survey.approvalFlow?.enabled;
  if (!isChangeApprovalOn) return false;
  if (!hasFeature(Features.SURVEYS_CHANGE_APPROVAL_V2)) return false;

  const fallbackUserAudience = getFallbackUserAudience(survey.approvalFlow);
  return !fallbackUserAudience.audiencesIds.some(userId =>
    users.some(user => user._id === userId)
  );
};

const checkIfEmptyUserGroup = (
  survey: MaybePersistedSurvey,
  groupsById: Record<ArdoqId, PermissionGroup>
) => {
  const isChangeApprovalOn = survey.approvalFlow?.enabled;
  if (!isChangeApprovalOn || isEmpty(groupsById)) return false;
  if (!hasFeature(Features.SURVEYS_CHANGE_APPROVAL_V2)) return false;

  const usersAndGroupAudience = getUsersAndGroupsAudience(
    survey.approvalFlow?.selectors ?? []
  );

  if (!hasUserAndGroupsAudience(usersAndGroupAudience)) return;
  return usersAndGroupAudience.groupIds.some(
    userGroupId => !groupsById?.[userGroupId].users.length
  );
};

const hasAccessToSelectedResultFilteringWorkspace = (
  survey: MaybePersistedSurvey,
  permissionsContext: PermissionContext
) => {
  const { enabled, type, properties } = survey.filterSetup ?? {};

  if (
    !enabled ||
    !properties?.workspaceId ||
    type !== SurveyResultFilteringType.REFERENCE
  )
    return true;
  return workspaceAccessControlInterface.canAccessWorkspace(
    permissionsContext,
    properties?.workspaceId
  );
};

export const isSelectedResultFilteringComponentTypeInvalid = (
  survey: MaybePersistedSurvey,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId
) => {
  const { enabled, type, properties } = survey.filterSetup ?? {};
  if (!enabled || type !== SurveyResultFilteringType.REFERENCE || !properties)
    return false;
  if (
    !properties.workspaceId ||
    !workspaceModelDataByWorkspaceId[properties.workspaceId]
  ) {
    // We do not consider it an error with the survey if the user cannot access the component type
    return false;
  }
  const { selectedTypeId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    properties.workspaceId,
    properties.componentTypeName
  );
  return (
    properties.componentTypeName &&
    Boolean(hasComponentType(properties.componentTypeName, selectedTypeId))
  );
};

type IsSelectedResultFilteringReferenceTypeInvalidArgs = {
  survey: MaybePersistedSurvey;
  existingWorkspaceIds: ReadonlySet<ArdoqId>;
} & Pick<ReferenceDataLookups, 'referenceTypesByWorkspaceId'>;

export const isSelectedResultFilteringReferenceTypeInvalid = ({
  survey,
  existingWorkspaceIds,
  referenceTypesByWorkspaceId,
}: IsSelectedResultFilteringReferenceTypeInvalidArgs) => {
  const { enabled, type, properties } = survey.filterSetup ?? {};
  if (!enabled || type !== SurveyResultFilteringType.REFERENCE || !properties) {
    return false;
  }
  if (!properties.referenceTypeName) return false;
  const referenceWorkspaceId = properties.outgoing
    ? survey.workspace
    : properties.workspaceId;
  if (
    !referenceWorkspaceId ||
    !existingWorkspaceIds.has(referenceWorkspaceId)
  ) {
    // We do not consider it an error with the survey if the workspace is inaccessible
    return false;
  }
  const referenceTypes =
    referenceTypesByWorkspaceId[referenceWorkspaceId] ?? [];
  return !referenceTypes.some(
    refType => refType.name === properties.referenceTypeName
  );
};

const isSurveyComponentTypeInvalid = (
  userHasAdminAccessToSurveyWorkspace: boolean,
  survey: MaybePersistedSurvey,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId
) => {
  const { selectedTypeId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    survey.workspace,
    survey.componentTypeName
  );
  return (
    userHasAdminAccessToSurveyWorkspace &&
    Boolean(hasComponentType(survey.componentTypeName, selectedTypeId))
  );
};

export const getSurveyErrorsAndWarnings = async (
  survey: MaybePersistedSurvey,
  tags: APITagAttributes[] | null,
  permissionsContext: PermissionContext,
  users: APIOrganizationUser[],
  groupsById: Record<ArdoqId, PermissionGroup>,
  referenceDataLookups: ReferenceDataLookups,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId,
  existingWorkspaceIds: ReadonlySet<ArdoqId>,
  fieldsState: FieldsState
): Promise<SurveyValidation> => {
  const surveyId = isPersistedSurvey(survey) ? survey._id : '';
  const misc = survey.misc;
  const errorCodes = new Set<SurveyErrorName>();
  const warningCodes = new Set<SurveyWarningName>();
  const { referenceTypesByWorkspaceId } = referenceDataLookups;

  const userCanEditSurvey =
    isPersistedSurvey(survey) &&
    (!survey.workspace ||
      workspaceAccessControlInterface.canAdminWorkspace(
        permissionsContext,
        survey.workspace,
        null
      )) &&
    surveyAccessControlInterface.canEditSurvey(getFeatureContext(), survey._id);

  const userCanCreateAndIsCreatingSurvey =
    !isPersistedSurvey(survey) &&
    surveyAccessControlInterface.canCreateSurvey(getFeatureContext());

  if (!userCanEditSurvey && !userCanCreateAndIsCreatingSurvey) {
    return {
      _id: surveyId,
      misc,
      errorCodes,
      warningCodes,
      questionValidations: [],
    };
  }

  if (
    hasHiddenFieldsError(survey, workspaceModelDataByWorkspaceId, fieldsState)
  ) {
    errorCodes.add(SurveyErrorName.HIDDEN_FIELDS);
  }

  if (!survey.name?.trim()) {
    errorCodes.add(SurveyErrorName.NAME_INVALID);
  }

  if (survey.contactEmail) {
    const isValidEmail = isEmail(survey.contactEmail);
    const isUrl = isValidUrl(survey.contactEmail);

    if (!isValidEmail && !isUrl) {
      errorCodes.add(SurveyErrorName.CONTACT_EMAIL_INVALID);
    }
  }

  const userHasAdminAccessToSurveyWorkspace =
    !currentUserHasNoAdminAccessToWorkspace(
      permissionsContext,
      survey.workspace
    );

  if (!survey.workspace) {
    errorCodes.add(SurveyErrorName.WORKSPACE_INVALID);
  } else if (!userHasAdminAccessToSurveyWorkspace) {
    errorCodes.add(SurveyErrorName.NO_ADMIN_ACCESS_TO_SURVEY_WORKSPACE);
  } else if (!existingWorkspaceIds.has(survey.workspace)) {
    errorCodes.add(SurveyErrorName.WORKSPACE_INVALID);
  }

  const componentTypeInvalid = isSurveyComponentTypeInvalid(
    userHasAdminAccessToSurveyWorkspace,
    survey,
    workspaceModelDataByWorkspaceId
  );

  if (componentTypeInvalid) {
    errorCodes.add(SurveyErrorName.COMPONENT_TYPE_INVALID);
  }

  if (
    isRequiredNewComponentParentMissing(survey, workspaceModelDataByWorkspaceId)
  ) {
    errorCodes.add(SurveyErrorName.REQUIRED_NEW_COMPONENT_PARENT_IS_MISSING);
  }

  const selectedNewComponentParentDoesNotExist =
    await checkIfSelectedNewComponentParentDoesNotExist(survey);

  if (selectedNewComponentParentDoesNotExist) {
    errorCodes.add(
      SurveyErrorName.SELECTED_NEW_COMPONENT_PARENT_DOES_NOT_EXIST
    );
  }

  if (
    !hasAccessToSelectedResultFilteringWorkspace(survey, permissionsContext)
  ) {
    warningCodes.add(
      SurveyWarningName.NO_ACCESS_TO_SELECTED_RESULT_FILTERING_WORKSPACE
    );
  }

  if (
    isSelectedResultFilteringComponentTypeInvalid(
      survey,
      workspaceModelDataByWorkspaceId
    )
  ) {
    errorCodes.add(
      SurveyErrorName.SELECTED_RESULT_FILTERING_COMPONENT_TYPE_INVALID
    );
  }

  if (
    isSelectedResultFilteringReferenceTypeInvalid({
      survey,
      existingWorkspaceIds,
      referenceTypesByWorkspaceId,
    })
  ) {
    errorCodes.add(
      SurveyErrorName.SELECTED_RESULT_FILTERING_REFERENCE_TYPE_INVALID
    );
  }

  if (isMissingResultFilteringFields(survey)) {
    errorCodes.add(SurveyErrorName.MISSING_RESULT_FILTERING_FIELDS);
  }

  if (isMissingNameQuestion(survey)) {
    errorCodes.add(SurveyErrorName.MISSING_NAME_QUESTION);
  }

  const questionValidations = await getQuestionValidations({
    survey,
    tags,
    permissionsContext,
    referenceDataLookups,
    workspaceModelDataByWorkspaceId,
    fieldsState,
  });
  const questionErrorsExist = questionValidations.some(validations =>
    hasQuestionValidationOfSeverity(validations, 'error')
  );
  const questionWarningsExist = questionValidations.some(validations =>
    hasQuestionValidationOfSeverity(validations, 'warning')
  );

  if (questionErrorsExist) {
    errorCodes.add(SurveyErrorName.QUESTION_ERRORS_EXIST);
  }

  if (questionWarningsExist) {
    warningCodes.add(SurveyWarningName.QUESTION_WARNINGS_EXIST);
  }

  if (checkHasChangeApprovalCompatibilityError(survey)) {
    errorCodes.add(SurveyErrorName.CHANGE_APPROVAL_COMPATIBILITY);
  }

  if (checkHasAdvancedAudienceSelectionError(survey)) {
    errorCodes.add(SurveyErrorName.CHANGE_APPROVAL_INVALID_ADVANCED_AUDIENCE);
  }

  if (checkIfMissingFallbackUser(survey, users)) {
    errorCodes.add(SurveyErrorName.CHANGE_APPROVAL_FALLBACK_USER);
  }

  if (checkIfEmptyUserGroup(survey, groupsById)) {
    warningCodes.add(SurveyWarningName.CHANGE_APPROVAL_EMPTY_USER_GROUP);
  }

  return {
    _id: surveyId,
    misc,
    errorCodes,
    warningCodes,
    questionValidations,
  };
};

export const canAccessWorkspace = (
  permissionContext: PermissionContext,
  workspaceId?: ArdoqId | null,
  workspaceDoesNotExistError?: string,
  workspaceAccessError?: string
) => {
  if (
    workspaceId &&
    workspaceAccessControlInterface.canAccessWorkspace(
      permissionContext,
      workspaceId
    )
  )
    return true;
  return workspaceId && !workspaceDoesNotExistError && !workspaceAccessError;
};

export const shouldDisplayErrorMessageForField = (
  entityIsPersisted: boolean,
  sectionStatus: SurveySectionStatus | null,
  fieldHasLostFocus: boolean | undefined
) => {
  return (
    entityIsPersisted || sectionStatus === 'left' || fieldHasLostFocus || false
  );
};

const SURVEY_BASE_PATH = '/surveys/survey';
const LOCAL_SURVEY_PORT = 4100;

const getSurveyPath = (
  surveyId: ArdoqId,
  componentId?: ArdoqId | 'new'
): string =>
  componentId
    ? `${SURVEY_BASE_PATH}/${surveyId}/component/${componentId}`
    : `${SURVEY_BASE_PATH}/${surveyId}`;

/**
 * An absolute URL for a path served by the surveys app, which runs on its own
 * dev server in development mode.
 */
export const getSurveysAppUrl = (path: string): string =>
  isDevelopmentMode()
    ? `http://${window.location.hostname}:${LOCAL_SURVEY_PORT}${path}`
    : path;

export const getSurveyUrl = (
  surveyId: ArdoqId,
  componentId?: ArdoqId | 'new'
): string => getSurveysAppUrl(getSurveyPath(surveyId, componentId));
